import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export interface ChatSessionInfo {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export type ChatMessageType =
  | 'USER_MESSAGE'
  | 'AGENT_MESSAGE'
  | 'TOOL_CALL'
  | 'TOOL_OUTPUT';

export interface ChatEvent {
  id: string;
  eventType: ChatMessageType;
  content: string;
  createdAt: string;
}

export interface SessionsResponse {
  sessions: ChatSessionInfo[];
}

export interface MessagesResponse {
  messages: ChatEvent[];
}

export interface CreateSessionResponse {
  id: string;
  title: string;
}

export interface RunAgentResponse {
  response: string;
}

const queryKeys = {
  all: () => ['agent', 'chat'] as const,
  sessions: () => [...queryKeys.all(), 'sessions'] as const,
  messages: (sessionId: string) =>
    [...queryKeys.all(), 'messages', sessionId] as const,
};

export const useGetSessions = () => {
  return useQuery<SessionsResponse, ErrorResponse>({
    queryKey: queryKeys.sessions(),
    queryFn: async () => {
      return await fetchAPI<SessionsResponse>('v1/agent/sessions');
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateSession = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateSessionResponse, ErrorResponse, { title?: string }>({
    mutationFn: async ({ title = '' }) => {
      return await fetchAPI<CreateSessionResponse>('v1/agent/sessions', {
        method: 'POST',
        body: { title },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions() });
    },
  });
};

export const useGetMessages = (sessionId: string) => {
  return useQuery<MessagesResponse, ErrorResponse>({
    queryKey: queryKeys.messages(sessionId),
    queryFn: async () => {
      return await fetchAPI<MessagesResponse>(`v1/agent/sessions/${sessionId}`);
    },
    enabled: !!sessionId,
    placeholderData: keepPreviousData,
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean },
    ErrorResponse,
    { sessionId: string }
  >({
    mutationFn: async ({ sessionId }) => {
      return await fetchAPI<{ success: boolean }>(
        `v1/agent/sessions/${sessionId}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions() });
    },
  });
};

export const useRunAgent = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<RunAgentResponse, ErrorResponse, { message: string }>({
    mutationFn: async ({ message }) => {
      return await fetchAPI<RunAgentResponse>(
        `v1/agent/sessions/${sessionId}/run`,
        {
          method: 'POST',
          body: { message },
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.messages(sessionId),
      });
    },
  });
};
